import React, { FC, useCallback, useEffect, useState } from 'react';

import engineeringHiringBg from '@rover/app/images/global_announcements/bg-banner-tech.png';
import Announcement from '@rover/react-lib/src/components/announcements/Announcement';
import { GlobalAnnouncements as GlobalAnnouncement } from '@rover/rsdk/src/apiClient/latest';
import { CookiePurpose, createCookie } from '@rover/rsdk/src/modules/Network/cookies';
import { getCookie } from '@rover/rsdk/src/modules/Network/cookies/cookie.client';
import useGlobalAnnouncementsShim from '@rover/shared/js/hooks/useGlobalAnnouncementsShim';
import { AnnouncementType } from '@rover/types';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

const HIDE_ANNOUNCEMENT_COOKIE_DAYS = 365;

const ANNOUNCEMENT_TYPES = {
  'sitewide-emergency': AnnouncementType.DANGER,
  'covid19-intl-tier1': AnnouncementType.COVID19,
  'covid19-intl-tier2': AnnouncementType.COVID19,
  'covid19-intl-tier3': AnnouncementType.COVID19,
  'identity-verification-passed': AnnouncementType.IDENTITY_VERIFICATION_PASSED,
  'update-birthdate-required': AnnouncementType.UPDATE_BIRTHDATE_REQUIRED,
  'soft-launch-announcement': AnnouncementType.SOFT_LAUNCH_ANNOUNCEMENT,
};

const BACKGROUND_IMAGE_SRCS = {
  'engineering-hiring-announcement': engineeringHiringBg,
};

const findCurrentAnnouncement = (props: {
  globalAnnouncements?: GlobalAnnouncement[] | null;
  currentIndex: number;
}): GlobalAnnouncement | undefined => {
  const { globalAnnouncements, currentIndex } = props;
  const validAnnouncements = globalAnnouncements?.filter(
    ({ textDescription, textHeadline }) => textDescription || textHeadline
  );
  if (!validAnnouncements || currentIndex >= validAnnouncements.length) return undefined;
  return validAnnouncements[currentIndex];
};

const GlobalAnnouncements: FC = () => {
  const { globalAnnouncements } = useGlobalAnnouncementsShim();
  const [currentIndex, setCurrentIndex] = useState(0);

  const [currentAnnouncement, setCurrentAnnouncement] = useState<
    (GlobalAnnouncement & { edgeId?: string }) | null | undefined
  >(findCurrentAnnouncement({ globalAnnouncements, currentIndex }));

  useEffect(() => {
    setCurrentAnnouncement(findCurrentAnnouncement({ globalAnnouncements, currentIndex }));
  }, [globalAnnouncements, currentIndex]);

  const handleDismiss = useCallback(() => {
    setCurrentIndex(currentIndex + 1);

    if (currentAnnouncement) {
      createCookie(
        CookiePurpose.StrictlyNecessary,
        currentAnnouncement.isDismissedCookie,
        '1',
        HIDE_ANNOUNCEMENT_COOKIE_DAYS
      );
    }
  }, [currentIndex, currentAnnouncement]);

  const handleClick = useCallback(() => {
    if (currentAnnouncement && currentAnnouncement.event) {
      const { event } = currentAnnouncement;
      fireDataLayerEvent({
        event,
      });
    }
  }, [currentAnnouncement]);

  if (!currentAnnouncement) {
    return null;
  }

  const {
    webAction,
    actionText,
    slug,
    isDismissible,
    isDismissedCookie,
    textDescription,
    textHeadline,
    edgeId,
  } = currentAnnouncement;
  let isDismissed = false;

  if (process.env.JS_ENV_CLIENT && !!isDismissedCookie) {
    isDismissed = getCookie(isDismissedCookie) === '1';
  }

  return isDismissed ? null : (
    <Announcement
      actionLinkPath={webAction}
      actionText={actionText}
      announcementType={ANNOUNCEMENT_TYPES[slug] || AnnouncementType.INFO}
      backgroundImageSrc={BACKGROUND_IMAGE_SRCS[slug]}
      isDismissible={isDismissible}
      key={slug}
      onClickAction={handleClick}
      onDismiss={handleDismiss}
      textDescription={textDescription}
      textHeadline={textHeadline}
      edgeId={edgeId}
    />
  );
};

export default GlobalAnnouncements;
